import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Button from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import AddIcon from '@atlaskit/icon/glyph/add';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import InlineMessage from '@atlaskit/inline-message';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { R400 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import { Maybe } from '@css-xp/graphtypes';
import { UpdateTransactionAccountPayload } from '@atlassian/cssxp-types-billing';
import { Permissions } from '@atlassian/cssxp-typescommon';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { DynamicResultTable, OpenSidebarButton } from '../..';
import { useUpdateTransactionAccount } from '../../hooks';
import { isAccess } from '../../security';
import BillingAdminPerson from './billing-admin-person';
import './billing-admin.scss';
import type { Admin, BillingAdminsProps } from './types';

// import { CustomSearch } from '../../custom-search';
const DEFAULT_ROWS_PER_PAGE = 8;
const DEFAULT_PAGE = 1;

function getDataForCurrentPage(data: Admin[], currentPage: number, pageSize: number): Admin[] {
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  return data.slice(start, end);
}

const defaultEmptystateProps = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
};

interface CheckBoxStateType {
  email: Maybe<string>;
  isChecked: boolean;
  display: boolean;
}

const BillingAdmins = ({
  billingAdmins,
  loading,
  addBillingAdminsSidebarType,
  invoiceGroupId,
  transactionAccountId,
  billToAddress,
  onComplete,
  isSubtextHidden = false,
  addButtonAppearance = 'link',
  transactionAccountNumber,
  isRemoveDisabled = false,
  total,
  shouldForceAddBillingAdmin = false,
  sideBarClassName,
  validDomains,
  isPartnerAccount,
  emptyStateProps = defaultEmptystateProps,
}: BillingAdminsProps) => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [numberOfAdmins, setNumberOfAdmins] = useState<number>(0);
  // TODO :: Remove checking isAccess in the base component
  // P.S. This is being done in order to bypass the permissions as it is being used in PPC MVP
  const addBillingAdminPermission = isAccess(Permissions.CONTACT_WRITE) || shouldForceAddBillingAdmin;

  const [admins, setBillingAdmins] = useState<Admin[]>(getDataForCurrentPage(billingAdmins, currentPage, rowsPerPage));

  useEffect(() => {
    if (!loading) {
      setBillingAdmins(getDataForCurrentPage(billingAdmins, currentPage, rowsPerPage));
      setNumberOfAdmins(billingAdmins.length);

      setAdminsCheckBoxState(
        billingAdmins.map((billingAdmin) => ({ email: billingAdmin.email, isChecked: false, display: false }))
      );
    }
  }, [billingAdmins, currentPage, loading, rowsPerPage]);

  const { isProcessing, updateTransactionAccount } = useUpdateTransactionAccount({
    // eslint-disable-next-line
    onClose: () => {
      //do nothing
    },
    onComplete: () => {
      onComplete && onComplete();
    },
  });

  const [emailBeingRemoved, setEmailBeingRemoved] = useState<Maybe<string>[]>([]);
  const [adminsCheckboxState, setAdminsCheckBoxState] = useState<CheckBoxStateType[]>([]);

  const [isOpen, setIsOpen] = useState(false);
  const openModal = useCallback((emailsToRemove: Maybe<string>[]) => {
    setIsOpen(true);
    setEmailBeingRemoved([...emailBeingRemoved, ...emailsToRemove.filter((el) => el !== '')]);
  }, []);
  const closeModal = useCallback(() => setIsOpen(false), []);

  // const [searching, setSearching] = useState(false);
  // const [inSearchMode, setInSearchMode] = useState(false);
  // const [searchedAdmins, setSearchedAdmins] = useState<Admin[]>([]);

  // const onSearch = async (formData: FormData) => {
  //   setSearching(true);
  //   const input = formData && formData.searchTerm;
  //   if (input !== '') {
  //     const matchedBillingAdmins = billingAdmins.filter(
  //       (admin: Admin) => admin.fullName?.includes(input) || admin.email?.includes(input)
  //     );
  //     setBillingAdmins(getDataForCurrentPage(matchedBillingAdmins, 1, rowsPerPage));
  //     setNumberOfAdmins(matchedBillingAdmins.length);
  //     setInSearchMode(true);
  //     setSearchedAdmins(matchedBillingAdmins);
  //   } else {
  //     setBillingAdmins(getDataForCurrentPage(billingAdmins, 1, rowsPerPage));
  //     setNumberOfAdmins(billingAdmins.length);
  //     setInSearchMode(false);
  //     setSearchedAdmins([]);
  //   }
  //   setCurrentPage(1);
  //   setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
  //   setSearching(false);
  // };

  const onSetPage = (page: number) => {
    // TODO :: Use below line when search is enabled
    // setBillingAdmins(getDataForCurrentPage(inSearchMode ? searchedAdmins : billingAdmins, page, rowsPerPage));
    setBillingAdmins(getDataForCurrentPage(billingAdmins, page, rowsPerPage));
    setCurrentPage(page);
  };

  const handleCheckboxClick = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const clonedIsCheckedArray = [...adminsCheckboxState];
    if (event.target.checked) {
      const selectedIndex = clonedIsCheckedArray.findIndex((el) => el.email === value);
      clonedIsCheckedArray[selectedIndex] = { email: value, isChecked: true, display: true };
      setAdminsCheckBoxState(clonedIsCheckedArray);
    } else {
      const selectedIndex = clonedIsCheckedArray.findIndex((el) => el.email === value);

      clonedIsCheckedArray[selectedIndex] = { email: value, isChecked: false, display: false };
      setAdminsCheckBoxState(clonedIsCheckedArray);
    }
  };

  const handleCheckboxClickInModal = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const clonedIsCheckedArray = [...adminsCheckboxState];
    const clonedEmailBeingRemoved = [...emailBeingRemoved];

    if (event.target.checked) {
      const selectedIndex = clonedIsCheckedArray.findIndex((el) => el.email === value);
      clonedIsCheckedArray[selectedIndex] = { email: value, isChecked: true, display: true };
      setAdminsCheckBoxState(clonedIsCheckedArray);
      setEmailBeingRemoved([...emailBeingRemoved, value]);
    } else {
      const selectedIndex = clonedIsCheckedArray.findIndex((el) => el.email === value);
      clonedIsCheckedArray[selectedIndex] = { email: value, isChecked: false, display: true };
      setAdminsCheckBoxState(clonedIsCheckedArray);
      clonedEmailBeingRemoved.splice(clonedEmailBeingRemoved.indexOf(value), 1);
      setEmailBeingRemoved(clonedEmailBeingRemoved);
    }
  };

  const removeBillingAdmin = useCallback(() => {
    if (!transactionAccountId) {
      return;
    }
    const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {
      invoiceGroupId,
      modifiedBillingAdmins: {
        emailsOrAaidsToDelete: emailBeingRemoved as string[],
      },
    };

    updateTransactionAccount({
      transactionAccountId,
      updateTransactionAccountPayload,
    });
    closeModal();
  }, [closeModal, emailBeingRemoved, invoiceGroupId, transactionAccountId, updateTransactionAccount]);

  const makeRows = useCallback(() => {
    const isOnlySingleAdmin = total === 1;
    return admins.map((billingAdmin: Admin, index: number) => {
      const rowKey = `billing-admin-row-${index}`;
      return {
        key: rowKey,
        cells: [
          {
            key: `${rowKey}-details`,
            content: (
              <div className="bulk-admin-removal-container">
                {!isRemoveDisabled && !isOnlySingleAdmin ? (
                  <Checkbox
                    testId="admin-checkbox"
                    isChecked={
                      adminsCheckboxState[adminsCheckboxState.findIndex((el) => el.email === billingAdmin.email)]
                        ?.isChecked
                    }
                    onChange={handleCheckboxClick}
                    value={billingAdmin.email as string}
                    isDisabled={billingAdmin.isPartnerAdmin as boolean}
                    name={`${rowKey}-remove-admin`}
                    className="remove-bulk-admin-checkbox"
                  />
                ) : null}
                <BillingAdminPerson
                  fullName={billingAdmin.fullName}
                  email={billingAdmin.email}
                  isPartnerAdmin={billingAdmin.isPartnerAdmin}
                  picture={billingAdmin.picture ?? ''}
                />
              </div>
            ),
          },
        ],
      };
    });
  }, [admins, adminsCheckboxState, total]);

  const handleRowsChange = (rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
    setBillingAdmins(getDataForCurrentPage(billingAdmins, 1, rowsCount));
  };

  const isAllAdminsSelected = adminsCheckboxState.filter((el) => el.isChecked).length === numberOfAdmins;

  const renderRemoveBillingAdminButton = () => {
    return (
      <Button
        appearance="primary"
        isDisabled={isRemoveDisabled || isAllAdminsSelected}
        testId="remove-admin-btn"
        onClick={(_) => {
          openModal(adminsCheckboxState.map((checkedArr) => (checkedArr.isChecked ? checkedArr.email : '')));
        }}
      >
        Remove
      </Button>
    );
  };

  const renderDisabledRemoveButton = () => (
    <div className="remove-admin-btn-container">
      <Tooltip content="Select a billing admin first" position="top">
        <Button testId="remove-admin-btn" appearance="primary" isDisabled>
          Remove
        </Button>
      </Tooltip>
    </div>
  );

  const renderActionsOnTop = useCallback(() => {
    const isOnlySingleAdmin = total === 1;
    const isAdminSelected = !!adminsCheckboxState.find((el) => el.isChecked);

    return [
      () =>
        addBillingAdminsSidebarType && addBillingAdminPermission ? (
          <div className="billing-admin-actions-add-container">
            <div className="billing-admin-actions add">
              <div className={`add-action add-action-${addButtonAppearance}`}>
                <OpenSidebarButton
                  sidebar={addBillingAdminsSidebarType}
                  sidebarOptions={{
                    sidebarWidth: '600px',
                    isOverlay: true,
                  }}
                  payload={{
                    billingAdmins,
                    transactionAccountId,
                    invoiceGroupId,
                    billToAddress,
                    onComplete,
                    isSubtextHidden,
                    transactionAccountNumber,
                    shouldForceAddBillingAdmin,
                    validDomains,
                    isPartnerAccount,
                    sideBarClassName,
                  }}
                  appearance={addButtonAppearance}
                  iconBefore={<AddIcon label="Add icon" size="small" />}
                  testId="add-billing-admins"
                  label=" Add a billing admin"
                >
                  Add a billing admin
                </OpenSidebarButton>
              </div>
              {isSubtextHidden || (
                <span className="add-subtext">An active billing account requires at least one billing admin.</span>
              )}
            </div>

            {!isRemoveDisabled &&
              !isOnlySingleAdmin &&
              (!isAdminSelected ? (
                renderDisabledRemoveButton()
              ) : (
                <div className="remove-admin-btn-container">
                  {isAllAdminsSelected && (
                    <div>
                      <ErrorIcon size="small" label="" primaryColor={R400} />
                      <p className="remove-admin-warning">There must be at least one billing admin remaining</p>
                    </div>
                  )}
                  {renderRemoveBillingAdminButton()}
                </div>
              ))}
          </div>
        ) : (
          <div className="view-subtext-container">
            <span data-testid="actions-container-text" className="view-subtext">
              Billing admins linked to this account
            </span>
            {!isRemoveDisabled &&
              !isOnlySingleAdmin &&
              (!isAdminSelected ? (
                renderDisabledRemoveButton()
              ) : (
                <div className="remove-admin-btn-container">
                  {isAllAdminsSelected && (
                    <div>
                      <ErrorIcon size="small" label="" primaryColor={R400} />
                      <p className="remove-bulk-admin-warning">There must be at least one billing admin remaining</p>
                    </div>
                  )}
                  {renderRemoveBillingAdminButton()}
                </div>
              ))}
          </div>
        ),
    ];
  }, [
    addBillingAdminsSidebarType,
    billingAdmins,
    transactionAccountId,
    invoiceGroupId,
    billToAddress,
    onComplete,
    adminsCheckboxState,
    isRemoveDisabled,
    numberOfAdmins,
    isAllAdminsSelected,
  ]);

  const getRemoveBulkAdminModalText = useCallback(() => {
    const selectedAdminsCount = adminsCheckboxState.filter((el) => el.isChecked).length;
    return `Once you confirm, ${selectedAdminsCount} of ${numberOfAdmins} billing admins will be removed from ${transactionAccountNumber}. They’ll still be able to access other Atlassian products.
    `;
  }, [adminsCheckboxState, numberOfAdmins, transactionAccountNumber]);

  return (
    <>
      <ModalTransition>
        {isOpen && (
          <Modal onClose={closeModal} testId="remove-billing-admin-modal">
            <ModalHeader>
              <ModalTitle>
                <InlineMessage type="warning" secondaryText={`Are you sure you want to remove these billing admins?`} />
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              <div className="remove-admins-modal-container">
                <p>{getRemoveBulkAdminModalText()}</p>

                <div className="remove-admins-list">
                  <p>Selected billing admins</p>

                  <div style={{ position: 'relative' }}>
                    <div className="remove-admins-scroll-paper">
                      <div style={{ height: '100%' }}>
                        <ul className="remove-admins-scroll-list">
                          {adminsCheckboxState.map(({ email, display }, index) => {
                            const name = billingAdmins.find((billingAdmin) => billingAdmin.email === email)?.fullName;
                            return (
                              display && (
                                <li data-testid={`selected-admin`} key={index}>
                                  <div className="remove-admins-scroll-list-item">
                                    <Checkbox
                                      isChecked={
                                        adminsCheckboxState[
                                          adminsCheckboxState.findIndex(
                                            (el: { email: Maybe<string> }) => el.email === email
                                          )
                                        ]?.isChecked
                                      }
                                      onChange={handleCheckboxClickInModal}
                                      value={email as string}
                                    />
                                    <p>{`${name}, ${email}`}</p>
                                  </div>
                                </li>
                              )
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="remove-modal-footer">
                <Button appearance="subtle" onClick={closeModal}>
                  Cancel
                </Button>
                <Button appearance="warning" onClick={removeBillingAdmin} autoFocus testId="remove-billing-admin-btn">
                  Remove
                </Button>
              </div>
            </ModalFooter>
          </Modal>
        )}
      </ModalTransition>
      <div className="billing-admins-container">
        <div className="billing-admins-table">
          <DynamicResultTable
            isLoading={loading}
            rows={[...makeRows()]}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            loadingSpinnerSize="large"
            emptyStateProps={emptyStateProps}
            totalCount={numberOfAdmins}
            onPageChange={onSetPage}
            isPaginationEnabled={numberOfAdmins > DEFAULT_ROWS_PER_PAGE}
            onRowChange={handleRowsChange}
            actions={renderActionsOnTop()}
            boxShadow={false}
            className="billing-admins-table"
          >
            {/* TODO: Uncomment once search is completed */}
            {/* <CustomSearch
              placeholder={'Search all billing admins'}
              onSearch={onSearch}
              isLoading={loading || searching || isProcessing}
            /> */}
          </DynamicResultTable>
        </div>
      </div>
    </>
  );
};

export default BillingAdmins;
